import { AspectRatio } from "../model/Frame";

type VideoConstraints = {
    width: number;
    height: number;
}

/**
 * Converts AspectRatio enum to numeric ratio (width/height)
 */
export const aspectRatioToNumeric = (aspectRatio: AspectRatio): number => {
    switch (aspectRatio) {
        case AspectRatio.ONE_TO_ONE:
            return 1;
        case AspectRatio.FOUR_TO_THREE:
            return 4 / 3;
        case AspectRatio.THREE_TO_FOUR:
            return 3 / 4;
        case AspectRatio.FOUR_TO_FIVE:
            return 4 / 5;
        case AspectRatio.FIVE_TO_FOUR:
            return 5 / 4;
        default:
            return 1;
    }
}

/**
 * Crops an image to match the target aspect ratio, cropping from center.
 * This ensures the captured photo matches what the user sees with object-fit: cover.
 */
export function cropToAspectRatio(
    base64: string, 
    targetAspectRatio: AspectRatio
): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
            const targetRatio = aspectRatioToNumeric(targetAspectRatio);
            const srcRatio = img.width / img.height;
            
            let cropWidth = img.width;
            let cropHeight = img.height;
            let offsetX = 0;
            let offsetY = 0;
            
            if (Math.abs(srcRatio - targetRatio) < 0.01) {
                // Already correct ratio, return as-is
                resolve(base64);
                return;
            }
            
            if (srcRatio > targetRatio) {
                // Source is wider than target, crop sides
                cropWidth = img.height * targetRatio;
                offsetX = (img.width - cropWidth) / 2;
            } else {
                // Source is taller than target, crop top/bottom
                cropHeight = img.width / targetRatio;
                offsetY = (img.height - cropHeight) / 2;
            }
            
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
                reject(new Error('Failed to get canvas context'));
                return;
            }
            
            canvas.width = cropWidth;
            canvas.height = cropHeight;
            
            ctx.drawImage(
                img,
                offsetX, offsetY, cropWidth, cropHeight,  // Source rectangle
                0, 0, cropWidth, cropHeight               // Destination rectangle
            );
            
            resolve(canvas.toDataURL('image/jpeg', 0.92));
        };
        
        img.onerror = () => {
            reject(new Error('Failed to load image for cropping'));
        };
        
        img.src = base64;
    });
}

export const mapAspectRatioToVideoConstraints = (aspectRatio: AspectRatio): VideoConstraints => {
    switch (aspectRatio) {
        case AspectRatio.ONE_TO_ONE:
            return { width: 1024, height: 1024 };
        case AspectRatio.FOUR_TO_THREE:
            return { width: 1200, height: 900 };
        case AspectRatio.THREE_TO_FOUR:
            return { width: 900, height: 1200 };
        case AspectRatio.FOUR_TO_FIVE:
            return { width: 800, height: 1000 };
        case AspectRatio.FIVE_TO_FOUR:
            return { width: 1000, height: 800 };
        default:
            return { width: 1024, height: 1024 };
    }
}

export const mapAspectRatioToMaxWidthAndHeight = (aspectRatio: AspectRatio): string => {
    switch (aspectRatio) {
        case AspectRatio.ONE_TO_ONE:
            return "w-[300px] h-[300px]";
        case AspectRatio.FOUR_TO_THREE:
            return "w-[300px] h-[225px]";
        case AspectRatio.THREE_TO_FOUR:
            return "w-[225px] h-[300px]";
        case AspectRatio.FOUR_TO_FIVE:
            return "w-[240px] h-[300px]";
        case AspectRatio.FIVE_TO_FOUR:
            return "w-[300px] h-[240px]";
        default:
            return "w-[300px] h-[300px]";
    }
}